import React, { useEffect, useState } from 'react';
import { storeRAID, getRAID } from 'raid-js';

import InlineForm from './inlineForm';
import MultistepForm from './multistepForm';
import { useLogger, EVENTS } from '../../util/logger';
import '../../polyfills';

// separate questions from destinations
const parseProps = (props) => {
  const questions = [];
  const destinations = [];
  const analyticsAttributes = {};

  Object.entries(props).forEach(([name, text]) => {
    if (name.includes('question')) {
      questions.push({ name, text });
    } else if (name.includes('destination')) {
      const [label, url] = text.split('|');
      destinations.push({ name, label, url });
    } else if (name.includes('analytics')) {
      analyticsAttributes[name] = text;
    }
  });

  return [questions, destinations, analyticsAttributes];
};

const buildInitialState = (questions) =>
  questions.reduce((initialState, question) => {
    initialState[question.name] = null;
    return initialState;
  }, {});

const getTopDestination = ({ destinations, surveyData }) => {
  let topCount = 0;
  let topDestination = {};
  destinations.forEach((destination) => {
    // count how many times the user selected each destination
    const count = Object.values(surveyData).filter(
      (value) => value === destination.name
    ).length;
    // ties go to the left-most option,
    // so the count must be HIGHER than the previous best
    if (count > topCount) {
      topCount = count;
      topDestination = destination;
    }
  });
  return topDestination;
};

const UserRoutingSurvey = ({
  title,
  submitText = "Let's Go",
  apopheniaAppId,
  inline = 'false',
  ...props
}) => {
  const [questions, destinations, analyticsAttributes] = parseProps(props);

  const [startedSurvey, setStartedSurvey] = useState(false);
  const [surveyData, setSurveyData] = useState(buildInitialState(questions));

  const topDestination = getTopDestination({ destinations, surveyData });
  const formIncomplete = Object.values(surveyData).some((value) => !value);
  const isInline = inline === 'true'; // no way to pass boolean through data attributes
  const logger = useLogger(apopheniaAppId);

  useEffect(storeRAID, []);

  const handleChange = (event) => {
    // the first time a user clicks an input,
    // we want to log that they started the survey
    if (!startedSurvey) {
      setStartedSurvey(true);
      /* eslint-disable camelcase */
      logger.send(EVENTS.SURVEY_START_SUCCESS, {
        survey_id: title,
        user_id: getRAID()
      });
      /* eslint-enable camelcase */
    }

    setSurveyData({
      ...surveyData,
      [event.target.name]: event.target.value
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const surveyResponses = Object.values(surveyData).map(
      (value) => destinations.find((dest) => dest.name === value).label
    );
    /* eslint-disable camelcase */
    logger.send(EVENTS.SURVEY_COMPLETE_SUCCESS, {
      survey_id: title,
      user_id: getRAID(),
      survey_responses: surveyResponses,
      survey_result_id: topDestination.name,
      survey_result_description: topDestination.label,
      survey_result_value: topDestination.url
    });
    /* eslint-enable camelcase */

    window.location.assign(topDestination.url);
  };

  return (
    <form
      id="user-routing-survey-form"
      className={`UserRoutingSurvey ${
        isInline ? 'UserRoutingSurvey--inline' : ''
      }`}
      onSubmit={handleSubmit}
      data-analytics-form
      data-analytics-form-module-type={
        analyticsAttributes.analyticsFormModuleType
      }
      data-analytics-form-module-category={
        analyticsAttributes.analyticsFormModuleCategory
      }
      data-analytics-form-module-label={
        analyticsAttributes.analyticsFormModuleLabel
      }
    >
      <div className="u-cardLow UserRoutingSurvey-card">
        {isInline ? (
          <InlineForm
            title={title}
            submitText={submitText}
            questions={questions}
            destinations={destinations}
            handleChange={handleChange}
            formIncomplete={formIncomplete}
          />
        ) : (
          <MultistepForm
            title={title}
            submitText={submitText}
            questions={questions}
            destinations={destinations}
            surveyData={surveyData}
            handleChange={handleChange}
            formIncomplete={formIncomplete}
          />
        )}
      </div>
    </form>
  );
};

export default UserRoutingSurvey;
